import React, { useState, useRef, useCallback } from 'react';
import { Area } from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import { Camera, Trash2 } from 'lucide-react';
import {
  Button,
  Avatar,
  AvatarImage,
  AvatarFallback,
  getUserAvatarUrl,
  getUserInitials,
  cn
} from '@bliks-io/ui';
import { ImageCropper } from './ImageCropper';
import {
  useRequestAvatarUploadUrl,
  useConfirmAvatarUpload,
  useDeleteAvatar
} from '@/api/mutations/useAvatarUpload';
import { useProfile } from '@/api/queries/useProfile';
import { readFile, validateImageFile, getCroppedImg, uploadToPresignedUrl } from '@/lib/file-utils';
import { toast } from 'sonner';

/**
 * AvatarUpload Component
 *
 * A component for uploading and managing user avatars.
 * Features:
 * - Image selection via file input
 * - Image cropping with zoom controls
 * - Direct upload to DigitalOcean Spaces via presigned URLs
 * - Server-side image processing (multiple sizes and formats)
 * - Avatar deletion
 *
 * @example
 * ```tsx
 * function ProfileTab() {
 *   return (
 *     <div>
 *       <AvatarUpload />
 *       // Rest of profile form
 *     </div>
 *   );
 * }
 * ```
 */
export function AvatarUpload() {
  const { t } = useTranslation();
  const { data: user } = useProfile();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State for image cropping
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Mutations
  const requestUrl = useRequestAvatarUploadUrl();
  const confirmUpload = useConfirmAvatarUpload();
  const deleteAvatar = useDeleteAvatar();

  // Check if any operation is in progress
  const isLoading = isUploading || deleteAvatar.isPending;

  /**
   * Handle file selection
   */
  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      // Reset input value to allow selecting the same file again
      event.target.value = '';

      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast.error(validation.error || t('avatar.invalidFile'));
        return;
      }

      try {
        // Read file as data URL for cropper
        const imageDataUrl = await readFile(file);
        setSelectedImage(imageDataUrl);
        setShowCropper(true);
      } catch (error) {
        console.error('Failed to read file:', error);
        toast.error(t('avatar.fileReadError'));
      }
    },
    [t]
  );

  /**
   * Handle crop completion and upload
   */
  const handleCropComplete = useCallback(
    async (croppedArea: Area) => {
      if (!selectedImage) return;

      try {
        setIsUploading(true);
        // 1. Get the cropped image as a File
        const croppedFile = await getCroppedImg(selectedImage, croppedArea);

        // 2. Request presigned URL
        const presignedData = await requestUrl.mutateAsync();

        // 3. Upload to DigitalOcean Spaces
        await uploadToPresignedUrl(presignedData.url, presignedData.fields, croppedFile);

        // 4. Confirm upload to trigger server-side processing
        await confirmUpload.mutateAsync({ key: presignedData.key });

        // Success - close cropper
        setShowCropper(false);
        setSelectedImage(null);
      } catch (error) {
        console.error('Upload failed:', error);
        // Error toast is handled by the mutation hooks
      } finally {
        setIsUploading(false);
      }
    },
    [selectedImage, requestUrl, confirmUpload]
  );

  /**
   * Handle avatar deletion
   */
  const handleDelete = useCallback(async () => {
    if (!user?.avatar) return;

    try {
      await deleteAvatar.mutateAsync();
    } catch (error) {
      console.error('Delete failed:', error);
      // Error toast is handled by the mutation hook
    }
  }, [user?.avatar, deleteAvatar]);

  /**
   * Get avatar URL using utility function
   */
  const avatarUrl = getUserAvatarUrl(user, 'medium');

  /**
   * Get user initials for fallback using utility function
   */
  const userInitials = getUserInitials(user?.firstName, user?.lastName);

  return (
    <div className="relative w-fit">
      {/* Avatar Display */}
      <Avatar className="h-40 w-40" key={avatarUrl || 'no-avatar'}>
        {avatarUrl ? (
          <AvatarImage
            src={avatarUrl}
            alt={`${user?.firstName} ${user?.lastName}`}
            key={avatarUrl}
          />
        ) : null}
        <AvatarFallback className="text-4xl">{userInitials}</AvatarFallback>
      </Avatar>

      {/* Upload/Delete Buttons - Overlay */}
      <div
        className={cn(
          'absolute inset-0 flex items-center justify-center gap-2 bg-black/60 opacity-0 hover:opacity-100 transition-all duration-200 rounded-lg backdrop-blur-xs',
          isLoading && 'opacity-100'
        )}
      >
        <Button
          type="button"
          variant="primary"
          size="icon"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
          disabled={isLoading}
          title={user?.avatar ? t('avatar.change') : t('avatar.upload')}
          className="h-10 w-10 shadow-lg hover:scale-110 transition-transform bg-white/90 hover:bg-white text-primary"
        >
          <Camera className="h-5 w-5" />
        </Button>

        {user?.avatar && (
          <Button
            type="button"
            variant="destructive"
            size="icon"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDelete();
            }}
            disabled={isLoading}
            loading={deleteAvatar.isPending}
            title={t('avatar.delete')}
            className="h-10 w-10 shadow-lg hover:scale-110 transition-transform"
          >
            <Trash2 className="h-5 w-5" />
          </Button>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Image Cropper Dialog */}
      {selectedImage && (
        <ImageCropper
          imageSrc={selectedImage}
          open={showCropper}
          onClose={() => {
            setShowCropper(false);
            setSelectedImage(null);
          }}
          onCropComplete={handleCropComplete}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
